import { useState } from "react";
import { Badge, Button, Card, Flex, Heading, Table, Text } from "@radix-ui/themes";
import { useToast } from "context/ToastContext";
import { isSourceBusy, useKnowledgeSource, useKnowledgeSources, useKnowledgeSyncRuns, useSyncKnowledgeSource } from "hooks/aiAssistant/useKnowledge";
import { formatInterval, formatTime, formatType, KnowledgeDialog, KnowledgeLoadError, knowledgeErrorMessage,
  Pagination, PollingNotice, SafeSyncError, SourceHealth, SourceTimes } from "./KnowledgeShared";
import SourceForm from "./SourceForm";

function SyncButton({ source, onMessage }) {
  const sync = useSyncKnowledgeSource();
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState("");
  const click = async () => {
    if (submitting || sync.isPending) return;
    setSubmitting(true); setFailure("");
    try { await sync.mutateAsync(source.id); onMessage("Synchronization queued."); }
    catch (error) { setFailure(knowledgeErrorMessage(error, "sync")); }
    finally { setSubmitting(false); }
  };
  return <div><Button variant="soft" disabled={!source.enabled || isSourceBusy(source) || submitting || sync.isPending} onClick={click}>
    {submitting || sync.isPending ? "Queueing…" : "Sync now"}</Button>
    {failure && <p role="alert" className="knowledge-inline-error">{failure}</p>}</div>;
}

export function SourceRecords({ source }) {
  return <dl className="knowledge-records">
    <div><dt>Active records</dt><dd>{source.active_entity_count.toLocaleString()}</dd></div>
    <div><dt>Retrieval enabled</dt><dd>{source.retrieval_enabled_entity_count.toLocaleString()}</dd></div>
    <div><dt>Eligible for answers</dt><dd>{source.eligible_entity_count.toLocaleString()}</dd></div>
  </dl>;
}

export default function Sources({ adapters, onBrowse }) {
  const [page, setPage] = useState(1);
  const query = useKnowledgeSources({ page, limit: 20 });
  const [dialog, setDialog] = useState(null);
  const [notice, setNotice] = useState("");
  const { setToastMessage } = useToast();
  const message = text => { setNotice(text); setToastMessage(text); };
  const saved = (result, text) => { setDialog(null); message(text); };
  const catalogue = adapters.data || [];
  return <Flex direction="column" gap="4">
    <Flex justify="between" align="center" gap="3" wrap="wrap"><Text size="2" color="gray">Manage acquisition, synchronization and source health.</Text>
      <Flex gap="2"><Button variant="soft" color="gray" disabled={query.isFetching} onClick={query.refetch}>Refresh</Button>
        <Button disabled={!catalogue.length} onClick={() => setDialog({ type: "add" })}>Add knowledge source</Button></Flex></Flex>
    {notice && <Text role="status" size="2">{notice}</Text>}
    {adapters.isError && <KnowledgeLoadError error={adapters.error} onRetry={adapters.refetch} />}
    <PollingNotice query={query} />
    {query.isPending ? <Text role="status">Loading knowledge sources…</Text> : query.isError ? <KnowledgeLoadError error={query.error} onRetry={query.refetch} /> : <>
      {!query.data.items.length ? <Card className="knowledge-empty"><Heading size="4">No knowledge sources have been added yet.</Heading>
        <Text size="2" color="gray">Add an approved source to bring canonical knowledge into your Assistant.</Text>
        <Button disabled={!catalogue.length} onClick={() => setDialog({ type: "add" })}>Add knowledge source</Button></Card>
        : <div className="knowledge-source-grid">{query.data.items.map(source => <Card key={source.id} className="knowledge-source-card">
          <Flex direction="column" gap="4">
            <Flex justify="between" align="start" gap="3"><div><Heading size="4">{source.name}</Heading>
              <Text size="2" color="gray">{catalogue.find(item => item.key === source.adapter_type)?.display_name || formatType(source.adapter_type)}</Text></div><SourceHealth source={source} /></Flex>
            <Flex gap="2" wrap="wrap"><Badge color="gray">{formatType(source.entity_type)}</Badge><Badge color={source.enabled ? "green" : "gray"}>{source.enabled ? "Enabled" : "Disabled"}</Badge>
              <Badge color="gray">{source.sync_enabled ? "Auto sync" : "Auto-sync off"} · {formatInterval(source.sync_interval_minutes)}</Badge></Flex>
            <SourceRecords source={source} /><SourceTimes source={source} />
            {!!source.latest_sync_run?.records_invalid && <Text size="2" color="amber">{source.latest_sync_run.records_invalid} invalid records</Text>}
            <SafeSyncError error={source.last_error || source.latest_sync_run?.pipeline_error} />
            <Flex gap="2" wrap="wrap" align="start"><SyncButton source={source} onMessage={message} />
              <Button variant="soft" color="gray" onClick={() => setDialog({ type: "detail", sourceId: source.id })}>Details &amp; edit</Button>
              <Button variant="ghost" onClick={() => onBrowse(source)}>Browse knowledge</Button></Flex>
          </Flex>
        </Card>)}</div>}
      <Pagination data={query.data} onPage={setPage} busy={query.isFetching} />
    </>}
    {dialog?.type === "add" && <SourceForm adapters={catalogue} onClose={() => setDialog(null)} onSaved={saved} />}
    {dialog?.type === "detail" && <SourceDetail sourceId={dialog.sourceId} adapters={catalogue} onClose={() => setDialog(null)} onMessage={message} onBrowse={onBrowse} />}
  </Flex>;
}

function SourceDetail({ sourceId, adapters, onClose, onMessage, onBrowse }) {
  const query = useKnowledgeSource(sourceId);
  const [editing, setEditing] = useState(false);
  const [runsPage, setRunsPage] = useState(1);
  const runs = useKnowledgeSyncRuns(sourceId, { page: runsPage, limit: 10 });
  const source = query.data;
  if (editing && source) return <SourceForm source={source} adapters={adapters} onClose={() => setEditing(false)} onSaved={(result, text) => {
    setEditing(false); onMessage(text);
  }} />;
  return <KnowledgeDialog wide title={source?.name || "Knowledge source"} description="Source configuration, synchronization activity and recent history." onClose={onClose}>
    {query.isPending ? <Text role="status">Loading source…</Text> : query.isError ? <KnowledgeLoadError error={query.error} onRetry={query.refetch} /> : <Flex direction="column" gap="4">
      <Flex gap="3" wrap="wrap" align="center"><SourceHealth source={source} /><SyncButton source={source} onMessage={onMessage} />
        <Button variant="soft" color="gray" onClick={() => setEditing(true)}>Edit source</Button>
        <Button variant="soft" color="gray" disabled={query.isFetching || runs.isFetching} onClick={() => { query.refetch(); runs.refetch(); }}>Refresh</Button>
        <Button variant="ghost" onClick={() => { onClose(); onBrowse(source); }}>Browse knowledge</Button></Flex>
      <PollingNotice query={query} />
      <dl className="knowledge-facts">
        <div><dt>Adapter</dt><dd>{adapters.find(item => item.key === source.adapter_type)?.display_name || source.adapter_type}</dd></div>
        <div><dt>Entity type</dt><dd>{formatType(source.entity_type)}</dd></div><div><dt>Source type</dt><dd>{formatType(source.source_type)}</dd></div>
        <div><dt>Source URL{source.source_url_redacted ? " (query parameters hidden)" : ""}</dt><dd>{source.source_url || "Unavailable"}</dd></div>
        <div><dt>Enabled</dt><dd>{source.enabled ? "Yes" : "No"}</dd></div>
        <div><dt>Automatic synchronization</dt><dd>{source.sync_enabled ? "On" : "Off"} · {formatInterval(source.sync_interval_minutes)}</dd></div>
        <div><dt>Record ingestion</dt><dd>{formatType(source.sync_status)}</dd></div>
        <div><dt>Full synchronization</dt><dd>{formatType(source.latest_sync_run?.pipeline_status)}</dd></div>
        <div><dt>Historic records</dt><dd>{source.total_entity_count}</dd></div>
      </dl>
      <SourceRecords source={source} /><SourceTimes source={source} />
      <Text size="2" color="gray">Disabling stops acquisition and scheduling. Historic knowledge remains available to AI answers unless individual items are excluded.</Text>
      <SafeSyncError error={source.last_error || source.latest_sync_run?.pipeline_error} />
      <Heading size="4">Synchronization history</Heading>
      <SyncHistory query={runs} onPage={setRunsPage} />
    </Flex>}
  </KnowledgeDialog>;
}

function SyncHistory({ query, onPage }) {
  if (query.isPending) return <Text role="status">Loading synchronization history…</Text>;
  if (query.isError) return <KnowledgeLoadError error={query.error} onRetry={query.refetch} />;
  return <>
    {!query.data.items.length ? <Text size="2" color="gray">No synchronization runs yet.</Text> : <div className="knowledge-table-scroll">
      <Table.Root variant="surface"><Table.Header><Table.Row>
        {["Status", "Started", "Finished", "Received", "Created", "Updated", "Unchanged", "Deactivated", "Invalid", "Error"].map(label => <Table.ColumnHeaderCell key={label}>{label}</Table.ColumnHeaderCell>)}
      </Table.Row></Table.Header><Table.Body>{query.data.items.map(run => <Table.Row key={run.id}>
        <Table.RowHeaderCell><span>{formatType(run.pipeline_status || run.status)}</span>
          {run.pipeline_status && run.pipeline_status !== run.status && <div className="knowledge-muted">Records: {formatType(run.status)}</div>}
          {run.pipeline_stage && <div className="knowledge-muted">{formatType(run.pipeline_stage)}</div>}</Table.RowHeaderCell>
        <Table.Cell>{formatTime(run.started_at)}</Table.Cell><Table.Cell>{formatTime(run.pipeline_status ? run.pipeline_finished_at : run.finished_at)}
          {run.pipeline_status && run.finished_at && run.pipeline_finished_at !== run.finished_at
            && <div className="knowledge-muted">Records finished: {formatTime(run.finished_at)}</div>}</Table.Cell>
        {["received", "created", "updated", "unchanged", "deactivated", "invalid"].map(key => <Table.Cell key={key}>{run[`records_${key}`]}{key === "invalid" && run.records_invalid > 0 ? " invalid records" : ""}</Table.Cell>)}
        <Table.Cell><SafeSyncError error={run.pipeline_error || run.error} />{!run.pipeline_error && !run.error ? "—" : null}</Table.Cell>
      </Table.Row>)}</Table.Body></Table.Root>
    </div>}
    <Pagination data={query.data} onPage={onPage} busy={query.isFetching} />
    <Text size="2" color="gray">Invalid counts are supplied by synchronization. Record-level diagnostics are not available yet.</Text>
  </>;
}
